import React, { useState } from "react";
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Paper,
  makeStyles,
  Divider,
} from "@material-ui/core";
import t from "../common/localization";
import {
  formatDistance,
  formatPosition,
  formatHours,
  formatDate,
  formatVolume,
  formatDurationTime,
} from "../common/formatter";
import ReportFilter from "./ReportFilter";
import ReportLayoutPage from "./ReportLayoutPage";
import { useAttributePreference } from "../common/preferences";
import { showLoaderStateActions } from "./../store";
import { useDispatch, useSelector } from "react-redux";
import Chip from "@material-ui/core/Chip";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AcUnitIcon from "@material-ui/icons/AcUnit";
import LocalGasStationIcon from "@material-ui/icons/LocalGasStation";
import NavigationIcon from "@material-ui/icons/Navigation";
import AvTimerIcon from "@material-ui/icons/AvTimer";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import HourglassFullIcon from "@material-ui/icons/HourglassFull";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import { DataGrid } from "@material-ui/data-grid";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    width: "100%",
    borderRadius: "10px",
  },
  tableRow: {
    "&$hover:hover": {
      backgroundColor: "#FFFFFF",
    },
  },
  tCell: {
    color: "white",
    backgroundColor: "#361DDB",
  },
  tContainer: {
    maxHeight: 690,
  },
}));

const Filter = ({ setItems }) => {
  const dispatch = useDispatch();

  const handleSubmit = async (
    deviceId,
    groupId,
    from,
    to,
    mail,
    headers,
    onResponseGet
  ) => {
    const query = new URLSearchParams({ from, to, mail });
    let qString = query.toString();
    let deviceIdQuery = "";
    let groupIdQuery = "";

    deviceId.forEach((element) => {
      deviceIdQuery += `&deviceId=${element}`;
    });
    groupId.forEach((element) => {
      groupIdQuery += `&groupId=${element}`;
    });

    if (deviceIdQuery) {
      qString += deviceIdQuery;
    }
    if (groupIdQuery) {
      qString += groupIdQuery;
    }
    dispatch(showLoaderStateActions.addState(true));
    const response = await fetch(`/api/reports/stops?${qString}`, { headers });
    onResponseGet(response);
    if (response.ok && response.headers.get("content-type")) {
      const contentType = response.headers.get("content-type");
      if (contentType === "application/pdf") {
        const name = "report.pdf";
        const blobUrl = URL.createObjectURL(await response.blob());
        // Create a link element
        const link = document.createElement("a");
        // Set link's href to point to the Blob URL
        link.href = blobUrl;
        link.download = name;
        // Append link to the body
        document.body.appendChild(link);
        // Dispatch click event on the link
        // This is necessary as link.click() does not work on the latest firefox
        link.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
        // Remove link from body
        document.body.removeChild(link);
        dispatch(showLoaderStateActions.addState(false));
      } else if (contentType === "application/json") {
        const resItem = await response.json();
        dispatch(showLoaderStateActions.addState(false));
        setItems(resItem);
      } else {
        dispatch(showLoaderStateActions.addState(false));
        window.location.assign(
          window.URL.createObjectURL(await response.blob())
        );
      }
    } else {
      dispatch(showLoaderStateActions.addState(false));
    }
  };

  return (
    <ReportFilter handleSubmit={handleSubmit} reportHeader="Stop Report" />
  );
};

const StopReportPage = () => {
  const distanceUnit = useAttributePreference("distanceUnit");
  const [items, setItems] = useState([]);
  const classes = useStyles();

  let mainData = [];
  const setRecursiveData = (newItems) => {
    setTimeout(() => {
      let hasMore = mainData.length + 1 < newItems.length;
      const list = newItems.slice(0, mainData.length + 1);
      mainData = list;
      setItems(list);
      if (hasMore) setRecursiveData(newItems);
    }, 0);
  };

  const setData = (newItems) => {
    mainData = [];
    setRecursiveData(newItems);
  };

  const columns = [
    { field: "deviceName", headerName: t("settingsVehicleNumber"), flex: 1 },
    { field: "startTime", headerName: t("reportArrivalTime"), flex: 1 },
    { field: "endTime", headerName: t("reportDepartureTime"), flex: 1 },
    { 
      field: "startOdometer", 
      headerName: t("positionOdometer"),
      flex: 1,
      renderCell: (params) => <Chip
            label={params.value}
            icon={<NavigationIcon />}
            size="small"
            color="secondary"
            style={{
              backgroundColor: "#D8ECDB",
              color: "#29A639",
              borderRadius: 3,
              margin: "5px",
            }}
          />,
    },
    { 
      field: "duration", 
      headerName: t("reportDuration"),
      flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<AccessTimeIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#EFEBFC",
          color: "#623DE6",
          borderRadius: 3,
          margin: "5px",
        }}
      />,
    },
    { field: "engineHours", headerName: t("reportEngineHours"), flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<AvTimerIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#EFEBFC",
          color: "#623DE6",
          borderRadius: 3,
          margin: "5px",
        }}
      />
     },
    { field: "spentFuel",
      headerName: t("reportSpentFuel"), 
      flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<LocalGasStationIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#FFE9D1",
          color: "#FCAF25",
          borderRadius: 3,
          margin: "5px",
        }}
      />
     },
    { field: "airConditionerHours", headerName: t("reportACHours"), flex: 1,
      renderCell: (params) => <Chip
        label={params.value}
        icon={<AcUnitIcon />}
        size="small"
        color="secondary"
        style={{
          backgroundColor: "#E7F4DE",
          color: "#1798C2",
          borderRadius: 3,
          margin: "5px",
        }}
      />
     },
    { field: "address", headerName: t("positionAddress"), flex: 1, },
  ];

  const gridRows = items.map((item, index) => ({
    id: index, // required unique id for DataGrid
    deviceName: item.deviceName,
    startTime: formatDate(item.startTime, "MMM D, hh:mm A"),
    endTime: formatDate(item.endTime, "MMM D, hh:mm A"),
    startOdometer: formatDistance(item.startOdometer, distanceUnit),
    duration: formatDurationTime(item.duration),
    engineHours: formatDurationTime(item.engineHours),
    spentFuel: formatVolume(item.spentFuel),
    airConditionerHours: formatDurationTime(item.airConditionerHours),
    address: formatPosition(item, "address"),
  }));

  return (
    <ReportLayoutPage filter={<Filter setItems={setData} />}>
      {/* <TableContainer className={classes.tContainer} component={Paper}>
        <Table
          stickyHeader
          aria-label="sticky table"
          style={{ whiteSpace: "nowrap" }}
        >
          <TableHead style={{ backgroundColor: "#361DDB" }}>
            <TableRow>
              <TableCell className={classes.tCell}>
                {t("settingsVehicleNumber")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("reportArrivalTime")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("reportDepartureTime")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionOdometer")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("reportDuration")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("reportEngineHours")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("reportSpentFuel")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("reportACHours")}
              </TableCell>
              <TableCell className={classes.tCell}>
                {t("positionAddress")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow hover key={item.id} className={classes.tableRow}>
                <TableCell>
                  <b>{item.deviceName}</b>
                </TableCell>
                <TableCell>
                  {formatDate(item.startTime, "MMM D, hh:mm A")}
                </TableCell>
                <TableCell>
                  {formatDate(item.endTime, "MMM D, hh:mm A")}
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDistance(item.startOdometer, distanceUnit)}
                    icon={<NavigationIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#D8ECDB",
                      color: "#29A639",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDurationTime(item.duration)}
                    icon={<AccessTimeIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#EFEBFC",
                      color: "#623DE6",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDurationTime(item.engineHours)}
                    icon={<AvTimerIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#EFEBFC",
                      color: "#623DE6",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatVolume(item.spentFuel)}
                    icon={<LocalGasStationIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#FFE9D1",
                      color: "#FCAF25",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDurationTime(item.airConditionerHours)}
                    icon={<AcUnitIcon />}
                    size="small"
                    color="secondary"
                    style={{
                      backgroundColor: "#E7F4DE",
                      color: "#1798C2",
                      borderRadius: 3,
                      margin: "5px",
                    }}
                  />
                </TableCell>
                <TableCell>{formatPosition(item, "address")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer> */}
      <DataGrid
          rows={gridRows}
          columns={columns}
          disableSelectionOnClick
          autoHeight={false}
          pageSize={50}
          rowsPerPageOptions={[50, 100, 500]}
          style={{ height: "85vh", width: "100%", backgroundColor: "white" }}
          pagination
        />
    </ReportLayoutPage>
  );
};

export default StopReportPage;
